export interface ReceiptTextStyle {
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  extraLarge?: boolean;
}

export interface ReceiptTextSegment {
  text: string;
  style: ReceiptTextStyle;
}

const EMPTY_STYLE: ReceiptTextStyle = {};

type MarkupToken = "##" | "**" | "*" | "_" | "__";

const MARKUP_TOKENS: { token: MarkupToken; style: keyof ReceiptTextStyle }[] = [
  { token: "##", style: "extraLarge" },
  { token: "**", style: "bold" },
  { token: "__", style: "underline" },
  { token: "*", style: "italic" },
  { token: "_", style: "italic" },
];

function cloneStyle(style: ReceiptTextStyle): ReceiptTextStyle {
  return { ...style };
}

function toggleStyle(style: ReceiptTextStyle, key: keyof ReceiptTextStyle): ReceiptTextStyle {
  const next = cloneStyle(style);
  next[key] = !next[key];
  return next;
}

function findNextMarkupToken(
  input: string,
  index: number,
): { token: MarkupToken; style: keyof ReceiptTextStyle; index: number } | null {
  let best: { token: MarkupToken; style: keyof ReceiptTextStyle; index: number } | null = null;
  for (const candidate of MARKUP_TOKENS) {
    if (!input.startsWith(candidate.token, index)) {
      continue;
    }
    if (!best || candidate.token.length > best.token.length) {
      best = { ...candidate, index };
    }
  }
  return best;
}

export function parseReceiptMarkup(line: string): ReceiptTextSegment[] {
  const segments: ReceiptTextSegment[] = [];
  let style = cloneStyle(EMPTY_STYLE);
  let buffer = "";

  const flush = () => {
    if (!buffer) {
      return;
    }
    segments.push({ text: buffer, style: cloneStyle(style) });
    buffer = "";
  };

  for (let index = 0; index < line.length; ) {
    const markup = findNextMarkupToken(line, index);
    if (markup) {
      flush();
      style = toggleStyle(style, markup.style);
      index += markup.token.length;
      continue;
    }

    buffer += line[index];
    index += 1;
  }

  flush();
  return segments.length > 0 ? segments : [{ text: "", style: cloneStyle(EMPTY_STYLE) }];
}

export function stripReceiptMarkup(line: string): string {
  return parseReceiptMarkup(line)
    .map((segment) => segment.text)
    .join("");
}

export function visibleReceiptLength(line: string): number {
  return stripReceiptMarkup(line).length;
}

function pushSegment(
  segments: ReceiptTextSegment[],
  text: string,
  style: ReceiptTextStyle,
): void {
  if (!text) {
    return;
  }
  const last = segments[segments.length - 1];
  if (
    last &&
    last.style.bold === style.bold &&
    last.style.italic === style.italic &&
    last.style.underline === style.underline &&
    last.style.extraLarge === style.extraLarge
  ) {
    last.text += text;
    return;
  }
  segments.push({ text, style: cloneStyle(style) });
}

function splitIntoWords(segments: ReceiptTextSegment[]): ReceiptTextSegment[] {
  const words: ReceiptTextSegment[] = [];
  for (const segment of segments) {
    const parts = segment.text.split(/(\s+)/);
    for (const part of parts) {
      if (!part) {
        continue;
      }
      pushSegment(words, part, segment.style);
    }
  }
  return words;
}

export function wrapReceiptLine(line: string, width: number): string[] {
  const trimmed = line.trimEnd();
  if (!trimmed) {
    return [""];
  }

  const plain = stripReceiptMarkup(trimmed);
  if (plain.length <= width) {
    return [trimmed];
  }

  const words = splitIntoWords(parseReceiptMarkup(trimmed));
  const lines: string[] = [];
  let currentSegments: ReceiptTextSegment[] = [];
  let currentLength = 0;

  const serialize = (segments: ReceiptTextSegment[]): string => {
    let output = "";
    const active: ReceiptTextStyle = {};
    const closeAll = () => {
      if (active.extraLarge) {
        output += "##";
        active.extraLarge = false;
      }
      if (active.underline) {
        output += "__";
        active.underline = false;
      }
      if (active.italic) {
        output += "*";
        active.italic = false;
      }
      if (active.bold) {
        output += "**";
        active.bold = false;
      }
    };
    const openStyle = (style: ReceiptTextStyle) => {
      if (style.extraLarge && !active.extraLarge) {
        output += "##";
        active.extraLarge = true;
      }
      if (style.bold && !active.bold) {
        output += "**";
        active.bold = true;
      }
      if (style.italic && !active.italic) {
        output += "*";
        active.italic = true;
      }
      if (style.underline && !active.underline) {
        output += "__";
        active.underline = true;
      }
    };

    for (const segment of segments) {
      openStyle(segment.style);
      output += segment.text;
    }
    closeAll();
    return output.trimEnd();
  };

  const flushLine = () => {
    if (currentSegments.length === 0) {
      return;
    }
    lines.push(serialize(currentSegments));
    currentSegments = [];
    currentLength = 0;
  };

  for (const word of words) {
    const wordLength = stripReceiptMarkup(word.text).length;
    const isWhitespace = /^\s+$/.test(word.text);
    if (isWhitespace) {
      if (currentLength > 0 && currentLength + wordLength <= width) {
        pushSegment(currentSegments, word.text, word.style);
        currentLength += wordLength;
      }
      continue;
    }

    const extra = currentLength === 0 ? 0 : 1;
    if (currentLength > 0 && currentLength + extra + wordLength > width) {
      flushLine();
    }

    if (wordLength > width) {
      let rest = word.text;
      let restStyle = word.style;
      while (rest.length > width) {
        pushSegment(currentSegments, rest.slice(0, width), restStyle);
        flushLine();
        rest = rest.slice(width);
      }
      if (rest) {
        pushSegment(currentSegments, rest, restStyle);
        currentLength = rest.length;
      }
      continue;
    }

    if (currentLength > 0) {
      pushSegment(currentSegments, " ", EMPTY_STYLE);
      currentLength += 1;
    }
    pushSegment(currentSegments, word.text, word.style);
    currentLength += wordLength;
  }

  flushLine();
  return lines.length > 0 ? lines : [trimmed];
}

export function wrapReceiptText(text: string, width: number): string[] {
  return text.split("\n").flatMap((line) => wrapReceiptLine(line, width));
}

export function alignReceiptLine(
  line: string,
  width: number,
  alignment: "left" | "center" | "right",
): string {
  const plain = stripReceiptMarkup(line);
  if (plain.length >= width) {
    return line;
  }
  const padding = width - plain.length;
  if (alignment === "center") {
    const left = Math.floor(padding / 2);
    return `${" ".repeat(left)}${line}${" ".repeat(padding - left)}`;
  }
  if (alignment === "right") {
    return `${" ".repeat(padding)}${line}`;
  }
  return line;
}

export function applyTextareaFormat(
  value: string,
  selectionStart: number,
  selectionEnd: number,
  wrapper: "**" | "*" | "__",
): { value: string; selectionStart: number; selectionEnd: number } {
  const selected = value.slice(selectionStart, selectionEnd);
  const before = value.slice(0, selectionStart);
  const after = value.slice(selectionEnd);
  const wrapped = selected ? `${wrapper}${selected}${wrapper}` : `${wrapper}${wrapper}`;
  const nextValue = `${before}${wrapped}${after}`;
  const cursorStart = selected
    ? selectionStart + wrapper.length
    : selectionStart + wrapper.length;
  const cursorEnd = selected ? cursorStart + selected.length : cursorStart;
  return {
    value: nextValue,
    selectionStart: cursorStart,
    selectionEnd: cursorEnd,
  };
}
